import { Directive, ElementRef, Input, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appNumberInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputDirective),
      multi: true
    }
  ],
  standalone: false
})

// FIXME: there is a known bug when the appNumberInput directive
// is used on a reactive form control, and that control is disabled.
// the issue is the control does not actually get disabled. (sad trombone)
// the workaround is to add the [attr.disabled] attribute as follows:
//
// <input formControlName="controlName" class="form-control"
// appNumberInput [attr.disabled]="formName.controls.controlName.disabled ? 'disabled' : null"/>
//
// formName.controls can be shorted with a component getter such as
// get f() { return this.formName.controls } in the component.
// then in template it would be [attr.disabled]="f.controlName.disabled ? 'disabled' : null"
//
// for this extra and needless typing, I am truly sorry.
// if you can figure out how to resolve this, you get a cookie.
// --jb 2021-12-29
export class NumberInputDirective implements ControlValueAccessor {
  @Input() decimals = 2;
  @Input() allowNegative = false;
  @Input() allowSpacePress = false;

  constructor(private el: ElementRef) {}

  // the callback function to register on UI change
  onChange: any = () => {};
  onTouched: any = () => {};

  formatNumber(value, setFixed = true) {
    if (value !== undefined && value !== null && !isNaN(parseFloat(value))) {
      value = value.toString();
      // check to see if somehow we have more decimals than allowed
      if (
        this.decimals > 0 &&
        value.indexOf('.') > -1 &&
        value.split('.')[1].length > this.decimals
      ) {
        value = value.split('.')[0] + '.' + value.split('.')[1].slice(0, this.decimals);
      }

      if (setFixed) {
        value = parseFloat(value).toFixed(this.decimals);
      }
    } else {
      value = '';
    }

    return value;
  }

  formatPastedValue(pastedValue): string {
    let val = this.el.nativeElement.value;
    const start = this.el.nativeElement.selectionStart;
    const end = this.el.nativeElement.selectionEnd;
    let num = this.formatNumber(pastedValue, false);

    // user had some text selected when they pasted.
    // get what the resulting text if before the replace
    if (start !== end) {
      val = val.slice(0, start) + val.slice(end, val.length);
    }

    // allow 1 neg sign if one doesn't exist
    if (num.indexOf('-') > -1 && (val.indexOf('-') > -1 || !this.allowNegative)) {
      num = num.replace(/-/g, '');
    }

    if (num.indexOf('.') > -1 && (val.indexOf('.') > -1 || this.decimals == 0)) {
      num = num.replace(/./g, '');
    }

    // if pasting value after decimal place, trim value accordingly
    if (this.decimals > 0 && val.indexOf('.') > -1 && start > val.indexOf('.')) {
      const dec = val.split('.')[1].length;
      // user pasted values after a decimal place
      if (start === end && this.decimals == dec) {
        // no selection (so no text will be replaced) & value is at max decimal length
        num = '';
      } else if (start === end && this.decimals > dec) {
        // there is some available decimals left
        num = num.slice(0, this.decimals - dec);
      } else if (start !== end && this.decimals == dec) {
        // at max decimals, but user selected values after decimal
        num = num.slice(0, end - start);
      } else if (start !== end && this.decimals > dec) {
        // user made selection and there was some decimal space left
        const count = end - start + (this.decimals - dec);
        num = num.slice(0, count);
      }
    }

    return num;
  }

  writeValue(value: any) {
    value = this.formatNumber(value);
    this.onChange(value); // set form control value
    this.el.nativeElement.value = value; // update actual element value
  }

  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  @HostListener('change') update() {
    this.writeValue(this.el.nativeElement.value);
  }

  @HostListener('blur') touched() {
    //  mark form control as touched
    this.onTouched();
  }

  @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    let val = this.el.nativeElement.value;
    const start = this.el.nativeElement.selectionStart;
    const end = this.el.nativeElement.selectionEnd;

    if (val !== null && val !== undefined) {
      val = val.toString();
    }

    const disallowedEvents = [];

    if (event.key === '-') {
      if (!this.allowNegative || start !== 0 || val.indexOf('-') > -1) {
        // neg sign must be at the beginning. only 1 neg sign allowed
        event.preventDefault();
      }
    } else if (event.key === '.') {
      const decIdx = val.indexOf('.');
      if (decIdx > -1 && start <= decIdx && end >= decIdx) {
        // there is an existing decimal, but it's within selection
        // do not prevent
      } else if (this.decimals == 0 || decIdx > -1) {
        // allow 1 decimal point character if this.decimals > 0
        event.preventDefault();
      }
    } else if (!isNaN(parseInt(event.key, 10))) {
      // do not allow user to enter more than allowed decimals
      const decimals = val.indexOf('.') > -1 ? val.split('.')[1] : 0;

      if (
        val.indexOf('.') > -1 && // there is a decimal place present
        this.decimals > 0 && // ctrl allows decimals
        this.decimals == decimals.length && // at max decimal length
        start > val.indexOf('.') && // cursor is past the decimal point
        start == end /* no selection is made, so no values will be replaced */
      ) {
        event.preventDefault();
      }
    } else if (event.code === 'Space') {
      // for some uses, like the paygrid,
      // users have asked that pressing space clears the selected value of an input.
      if (!this.allowSpacePress) {
        event.preventDefault();
      } else {
        // allow space bar to function normally, and let formatNumber() handle number parsing
      }
    } else if (
      disallowedEvents.indexOf(event.key) > -1 ||
      (!event.ctrlKey && !event.metaKey && event.key.length === 1)
    ) {
      event.preventDefault();
    }
  }

  // NOTE: not working in firefox as of 2019-11-21
  // @HostListener('paste', ['$event']) onPaste(event: ClipboardEvent) {
  //   event.preventDefault();
  //   const pastedInput: string = event.clipboardData.getData('text/plain');
  //   console.log(document.execCommand('insertText', false, this.formatPastedValue(pastedInput)));
  // }

  @HostListener('mousewheel', ['$event']) onMousewheel(event: WheelEvent) {
    // prevent default HTML behavior where scrolling
    // inside number input increases or decreases the value
    event.preventDefault();
  }
}
